import type { ChartBlockData } from '@reports/domain/value-objects/blocks';

/**
 * Transformed chart data item for Recharts
 */
export interface TransformedChartData {
  name: string;
  value: number;
}

/**
 * Options for chart data transformation
 */
export interface TransformChartDataOptions {
  groupTopValues?: boolean;
}

const TOP_VALUES_COUNT = 5;
const MIN_ITEMS_FOR_GROUPING = 7;
const OTHER_LABEL_I18N_KEY = 'reportCommon:charts.other';

/**
 * Transforms chart block data into Recharts-compatible format
 *
 * Converts the domain ChartBlockData structure into the format expected by
 * Recharts library (name/value pairs).
 *
 * When groupTopValues is enabled and there are more than 6 items, it will:
 * - Sort data by value from highest to lowest
 * - Show the top 5 highest values
 * - Group the remaining values into an "Other" category
 *
 * @param {ChartBlockData | null | undefined} chartBlockData - Chart data from API
 * @param {Function} t - Translation function for i18n labels
 * @param {TransformChartDataOptions} [options] - Transformation options
 * @param {boolean} [options.groupTopValues=false] - Whether to group values beyond top 5
 * @returns {TransformedChartData[]} Array of transformed data items
 *
 * @example
 * // Basic usage without grouping
 * const data = transformChartData(chartBlockData, t);
 * // Returns: [{ name: 'January', value: 1500 }, { name: 'February', value: 2000 }]
 *
 * @example
 * // With grouping enabled (when data has 7+ items) - data is sorted by value descending
 * const data = transformChartData(chartBlockData, t, { groupTopValues: true });
 * // Returns: [
 * //   { name: 'Item1', value: 1500 },
 * //   { name: 'Item2', value: 1200 },
 * //   { name: 'Item3', value: 1000 },
 * //   { name: 'Item4', value: 800 },
 * //   { name: 'Item5', value: 600 },
 * //   { name: 'Otros', value: 400 }
 * // ]
 */
export const transformChartData = (
  chartBlockData: ChartBlockData | null | undefined,
  t: (key: string) => string,
  options?: TransformChartDataOptions,
): TransformedChartData[] => {
  if (!chartBlockData) {
    return [];
  }

  const transformedData = chartBlockData.values.map((item) => ({
    name: item.x,
    value: item.y[0],
  }));

  // Only group if groupTopValues is enabled and there are more than 6 items
  const shouldGroup = options?.groupTopValues === true && transformedData.length >= MIN_ITEMS_FOR_GROUPING;

  if (!shouldGroup) {
    return transformedData;
  }

  // Sort data by value from highest to lowest
  const sortedData = [...transformedData].sort((a, b) => b.value - a.value);

  // Take top 5 items with highest values
  const topValues = sortedData.slice(0, TOP_VALUES_COUNT);

  // Sum the remaining items
  const remainingValues = sortedData.slice(TOP_VALUES_COUNT);
  const otherValue = remainingValues.reduce((sum, item) => sum + item.value, 0);

  const otherCategory: TransformedChartData = {
    name: t(OTHER_LABEL_I18N_KEY),
    value: otherValue,
  };

  return [...topValues, otherCategory];
};
